import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import { Check, CreditCard, ArrowLeft, Sparkles } from 'lucide-react';

const SubscribePage = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [billingInterval, setBillingInterval] = useState('month');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const pricing = {
    month: {
      price: 49.99,
      display: '$49.99',
      period: 'month',
      periodLabel: 'per month',
      buttonText: 'Subscribe - $49.99/mo'
    },
    year: {
      price: 549,
      display: '$549',
      period: 'year',
      periodLabel: 'per year',
      buttonText: 'Subscribe - $549/yr',
      savings: 'Save $50 with annual billing'
    }
  };

  const currentPricing = pricing[billingInterval];

  const features = [
    'Unlimited deal tracking',
    'Agent P&L Tracker',
    'Commission Cap Tracker',
    'Goal Settings & Progress',
    'AI-powered coaching insights',
    'Social media post generator',
    'Branded PDF reports',
    'All calculators & tools'
  ];

  const handleSubscribe = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const baseUrl = window.location.origin;
      
      const response = await axios.post(
        `${API_BASE_URL}/api/clerk/create-checkout`,
        {
          clerk_user_id: user.id,
          plan: 'pro',
          interval: billingInterval,
          email: user.primaryEmailAddress?.emailAddress,
          success_url: `${baseUrl}/dashboard?checkout=success&session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${baseUrl}/subscribe?checkout=cancelled`
        },
        {
          withCredentials: true,
          timeout: 15000
        }
      );
      
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.response?.data?.detail || 'Unable to start checkout. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <button 
            onClick={() => navigate('/dashboard')}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Title */}
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            Continue Your Journey
          </h1>
          <p className="text-lg text-gray-600 max-w-xl mx-auto">
            Keep all your tools, data, and AI coaching by subscribing today.
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden max-w-lg mx-auto">
          
          {/* Billing Toggle */}
          <div className="bg-gray-50 p-6 border-b border-gray-100">
            <div className="flex items-center justify-center">
              <div className="bg-white rounded-full p-1 shadow-sm border border-gray-200 flex items-center">
                <button
                  onClick={() => setBillingInterval('month')}
                  className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all ${
                    billingInterval === 'month'
                      ? 'bg-[#2FA163] text-white shadow-md'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  data-testid="monthly-btn"
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingInterval('year')}
                  className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all flex items-center gap-2 ${
                    billingInterval === 'year'
                      ? 'bg-[#2FA163] text-white shadow-md'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  data-testid="annual-btn"
                >
                  Annual
                  {billingInterval !== 'year' && (
                    <span className="bg-emerald-100 text-emerald-700 text-xs px-2 py-0.5 rounded-full">
                      Save $50
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="p-8 text-center">
            <div className="mb-2">
              <span className="text-5xl font-bold text-gray-900">{currentPricing.display}</span>
              <span className="text-xl text-gray-500 ml-2">{currentPricing.periodLabel}</span>
            </div>
            
            {billingInterval === 'year' && (
              <p className="text-emerald-600 font-medium mb-6">
                {currentPricing.savings}
              </p>
            )}
            
            {billingInterval === 'month' && (
              <p className="text-gray-500 text-sm mb-6">
                Billed monthly. Cancel anytime.
              </p>
            )}

            {/* Features */}
            <div className="text-left mb-8">
              <p className="text-sm font-semibold text-gray-900 mb-4">Everything you need:</p>
              <ul className="space-y-3">
                {features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#2FA163] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Subscribe Button */}
            <button
              onClick={handleSubscribe}
              disabled={isLoading}
              className="w-full bg-[#2FA163] hover:bg-[#268a54] text-white font-semibold py-4 px-6 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-lg"
              data-testid="subscribe-btn"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5" />
                  {currentPricing.buttonText}
                </>
              )}
            </button>

            <p className="text-xs text-gray-500 mt-4">
              Secure payment powered by Stripe
            </p>
          </div>
        </div>

        {/* Trust badges */}
        <div className="flex items-center justify-center gap-6 mt-8 text-gray-400 text-sm">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            <span>Cancel anytime</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4" />
            <span>Instant access</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscribePage;
